<script>
import NavBar from '@components/nav-bar.vue'
import SignOut from '@components/sign-out.vue'
import AppSwitcher from '@components/app-stream/app-switcher.vue'
import FigmentDrawer from '@components/drawer/figment-drawer.vue'
import { DateTime } from 'luxon'
import { formatPhoneNumber } from '@utils/phone-format'
import { getHardwareTimezone } from '@utils/hardware'

export default {
  components: { AppSwitcher, FigmentDrawer, NavBar, SignOut },
  props: {
    containers: {
      type: Array,
      required: true,
    },
    figment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      popoverVisible: false,
      drawerVisible: false,
      time:
        DateTime.now()
          .setZone('America/New_York')
          .toLocaleString(DateTime.TIME_WITH_SHORT_OFFSET) || new Date(),
    }
  },
  mounted() {
    this.startTime()
  },
  methods: {
    format: formatPhoneNumber,
    onShowDrawer(bool) {
      this.drawerVisible = bool
    },
    showDrawer() {
      this.drawerVisible = true
    },
    hidePopover() {
      this.popoverVisible = false
    },
    viewFigments() {
      this.$router.push({
        name: 'figments',
      })
    },
    editFigment() {
      const figmentId = this.figment.pk.replace('FIGMENT#', '')
      const routeData = this.$router.resolve({
        name: 'figment-profile',
        params: { figmentId },
      })
      window.open(routeData.href, '_blank')
    },
    onCollapse(collapsed, type) {},
    onBreakpoint(broken) {},
    startTime() {
      const tz = getHardwareTimezone(this?.figment?.meta?.sims?.hardwareId)
      this.time =
        DateTime.now()
          .setZone(tz)
          .toLocaleString(DateTime.TIME_WITH_SHORT_OFFSET) || new Date()
      setTimeout(this.startTime, 1000)
    },
  },
}
</script>

<template>
  <a-layout id="components-layout-demo-responsive">
    <a-layout-sider
      breakpoint="lg"
      collapsed-width="0"
      :collapsible="true"
      @collapse="onCollapse"
      @breakpoint="onBreakpoint"
    >
      <div class="logo" />
      <NavBar v-if="$auth.isAuthenticated && $auth.user.profile" />
      <div class="sign-out--container">
        <SignOut />
      </div>
    </a-layout-sider>
    <a-layout>
      <a-layout-header
        :style="{
          padding: '0 !important',
          height: '6rem',
          background: '#fff',
          overflow: 'hidden',
        }"
      >
        <div v-if="figment.meta" class="figment--profile">
          <a-card style="width: 100%">
            <template slot="actions" class="ant-card-actions">
              <a-icon
                key="info-circle"
                type="info-circle"
                @click="showDrawer"
              />
              <a-icon key="setting" type="setting" @click="editFigment" />
              <a-popover
                v-model="popoverVisible"
                title="Actions"
                trigger="click"
              >
                <p slot="content" @click="viewFigments"><a>Figments</a></p>
                <a slot="content" @click="hidePopover">Close</a>
                <a-icon key="ellipsis" type="ellipsis" />
              </a-popover>
            </template>
            <a-card-meta
              :title="`${figment.meta.bio.firstName} ${figment.meta.bio.lastName} · ${figment.meta.email.username}`"
              :description="
                `🇺🇸 ` +
                format(figment.meta.sims.pk.replace('SIM#', '')) +
                ` ▽ ${figment.meta.bio.currentLocation}  ${time}`
              "
            >
              <a-avatar
                v-if="figment.meta.bio.imageUrl"
                slot="avatar"
                size="small"
                :src="figment.meta.bio.imageUrl"
              />
              <a-avatar v-else slot="avatar" size="small" icon="user" />
            </a-card-meta>
          </a-card>
        </div>

        <div
          :style="{
            width: '100%',
            display: 'flex',
            overflowX: 'scroll',
            padding: '16px',
          }"
        >
          <div
            v-if="!containers.length"
            style="width: 100%; text-align: center"
          >
            <a-icon
              type="loading"
              style="font-size: 24px; color: #1890ff"
              spin
            />
          </div>
          <div
            v-else-if="containers.length"
            style="width: 100%; text-align: center"
          >
            <AppSwitcher
              :containers="containers"
              :style="{ padding: '24px' }"
            />
          </div>
          <div v-else style="width: 100%; text-align: center">
            <a style="margin: 0 auto" @click="editFigment">
              <a-icon type="exclamation-circle" style="margin-right: 5px" />
              No containers are assigned to this figment
            </a>
          </div>
        </div>
      </a-layout-header>
      <a-layout-content
        style="min-height: 0; margin: 24px"
        class="figment--layout"
      >
        <div v-if="figment.meta" class="warning--bar">
          <p>
            <a-icon type="solution" style="padding-right: 8px" />
            You are currently researching as Figment -
            {{ figment.meta.bio.firstName }}
            {{ figment.meta.bio.lastName }} &middot;
            {{ figment.meta.email.username }}
          </p>
        </div>
        <slot />
      </a-layout-content>
    </a-layout>
    <FigmentDrawer
      :visible="drawerVisible"
      :figment="figment"
      @showDrawer="onShowDrawer"
    ></FigmentDrawer>
  </a-layout>
</template>

<style lang="scss">
@import '@design';

.figment--profile {
  width: 33%;
  height: 6rem;

  .meta,
  .profile--image {
    display: inline-block;
  }

  .ant-card-meta-title {
    margin-bottom: 0 !important;
    font-size: 14px;
  }

  .ant-card-meta-description {
    margin-bottom: 0;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
    @media screen and (max-width: 1100px) {
      display: none;
    }
  }

  .ant-card-actions li {
    margin: 9px auto !important;
    @media screen and (max-width: 1100px) {
      margin: 16px auto !important;
    }
  }

  .ant-avatar {
    display: inline-block;
    margin: 0 auto;
    margin-right: 7px;
  }

  p {
    display: inline-block;
    padding: 0;
    margin: 0 auto;
  }
}

.figment--layout {
  #tabs--container,
  #app-stream--container {
    min-height: calc(100vh - 8.8rem - 28px);
    padding: 12px;
  }

  #appstream-container {
    &.active {
      iframe#appstream-streaming-session {
        min-height: calc(100vh - 12.3rem) !important;
      }
    }
  }
}

.warning--bar {
  width: 100%;
  text-align: center;
  background: #faad14;

  p {
    padding: 4px 0;
    margin: 0 auto;
  }
}

.container {
  min-width: $size-content-width-min;
  max-width: $size-content-width-max;
  margin: 0 auto;
}
</style>
